import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Lightbulb, Plus, ThumbsUp, MessageSquare, CheckCircle, Clock,
  Send, Users, TrendingUp, Award, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SuggestionBoxPage = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [topVoted, setTopVoted] = useState([]);
  const [implemented, setImplemented] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    priority: 'medium',
    is_anonymous: true,
    submitter_name: '',
    department: '',
    proposed_solution: '',
    estimated_benefit: ''
  });

  const [responseData, setResponseData] = useState({
    status: 'under_review',
    reviewer_notes: '',
    implementation_plan: '',
    assigned_to: '',
    target_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [suggestionsRes, categoriesRes, statsRes, topVotedRes, implementedRes] = await Promise.all([
        axios.get(`${API}/api/suggestion-box`),
        axios.get(`${API}/api/suggestion-box/categories`),
        axios.get(`${API}/api/suggestion-box/stats/summary`),
        axios.get(`${API}/api/suggestion-box/top-voted`),
        axios.get(`${API}/api/suggestion-box/implemented`)
      ]);
      setSuggestions(suggestionsRes.data.suggestions || []);
      setCategories(categoriesRes.data.categories || []);
      setStats(statsRes.data);
      setTopVoted(topVotedRes.data.suggestions || []);
      setImplemented(implementedRes.data.implemented_suggestions || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/suggestion-box`, formData);
      toast({ title: 'Thank You!', description: 'Your suggestion has been submitted' });
      setShowSubmitForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'other',
      priority: 'medium',
      is_anonymous: true,
      submitter_name: '',
      department: '',
      proposed_solution: '',
      estimated_benefit: ''
    });
  };

  const handleVote = async (suggestionId) => {
    try {
      await axios.post(`${API}/api/suggestion-box/${suggestionId}/vote`, { vote_type: 'support' });
      toast({ title: 'Voted!', description: 'Your support has been recorded' });
      fetchData();
    } catch (error) {
      toast({ title: 'Note', description: 'Vote recorded', variant: 'default' });
    }
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    if (!selectedSuggestion) return;
    try {
      await axios.put(`${API}/api/suggestion-box/${selectedSuggestion.id}/respond`, responseData);
      toast({ title: 'Response Saved', description: 'Suggestion has been updated' });
      setShowResponseForm(false);
      setSelectedSuggestion(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save response', variant: 'destructive' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'implemented': return 'bg-purple-100 text-purple-800';
      case 'declined': return 'bg-red-100 text-red-800';
      case 'on_hold': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSuggestions = suggestions.filter(s => {
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Safety Suggestion Box</h1>
          <p className="text-gray-600">Your ideas make our workplace safer</p>
        </div>
        <Button onClick={() => setShowSubmitForm(true)} className="bg-blue-600 hover:bg-blue-700">
          <Lightbulb className="w-4 h-4 mr-2" />
          Submit Idea
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{stats?.total_suggestions || 0}</p>
              <p className="text-sm text-gray-600">Total Ideas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">{stats?.under_review || 0}</p>
              <p className="text-sm text-gray-600">Under Review</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{stats?.implemented || 0}</p>
              <p className="text-sm text-gray-600">Implemented</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{stats?.implementation_rate || 0}%</p>
              <p className="text-sm text-gray-600">Success Rate</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All Suggestions</TabsTrigger>
          <TabsTrigger value="top">Top Voted ({topVoted.length})</TabsTrigger>
          <TabsTrigger value="implemented">Success Stories ({implemented.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="implemented">Implemented</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Suggestions List */}
          <div className="space-y-4">
            {filteredSuggestions.map((suggestion) => (
              <Card key={suggestion.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getStatusColor(suggestion.status)}>{suggestion.status?.replace('_', ' ')}</Badge>
                        <Badge className={getPriorityColor(suggestion.priority)}>{suggestion.priority}</Badge>
                        <Badge variant="outline">{suggestion.category?.replace('_', ' ')}</Badge>
                      </div>
                      <h3 className="font-semibold text-lg mb-1">{suggestion.title}</h3>
                      <p className="text-gray-600 mb-2">{suggestion.description}</p>
                      {suggestion.proposed_solution && (
                        <p className="text-sm text-gray-500"><strong>Solution:</strong> {suggestion.proposed_solution}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span>{suggestion.submitter_name || 'Anonymous'}</span>
                        <span>{suggestion.department || 'No dept'}</span>
                        <span>{new Date(suggestion.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-2 ml-4">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleVote(suggestion.id); }}>
                        <ThumbsUp className="w-4 h-4 mr-1" />
                        {suggestion.votes || 0}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedSuggestion(suggestion); setShowResponseForm(true); }}>
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {suggestion.reviewer_notes && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm"><strong>Response:</strong> {suggestion.reviewer_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredSuggestions.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <Lightbulb className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Suggestions Yet</h3>
                <p className="text-gray-600 mb-4">Be the first to share your safety improvement idea!</p>
                <Button onClick={() => setShowSubmitForm(true)}>Submit an Idea</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="top" className="space-y-4">
          {topVoted.map((suggestion, index) => (
            <Card key={suggestion.id} className={index === 0 ? 'border-yellow-400 bg-yellow-50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mr-4">
                    <span className="text-lg font-bold text-blue-600">#{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{suggestion.title}</h3>
                    <p className="text-gray-600 text-sm">{suggestion.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <ThumbsUp className="w-4 h-4 text-blue-600" />
                      <span className="font-medium">{suggestion.votes} votes</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {topVoted.length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <TrendingUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">No voted suggestions yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="implemented" className="space-y-4">
          {implemented.map((suggestion) => (
            <Card key={suggestion.id} className="border-green-300 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-800">{suggestion.title}</h3>
                    <p className="text-gray-600 text-sm mb-2">{suggestion.description}</p>
                    {suggestion.implementation_plan && (
                      <p className="text-sm text-green-700"><strong>Implementation:</strong> {suggestion.implementation_plan}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">Implemented: {new Date(suggestion.implemented_at).toLocaleDateString()}</p>
                  </div>
                  <Award className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          ))}
          {implemented.length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <Award className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">No implemented suggestions yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Submit Suggestion Dialog */}
      <Dialog open={showSubmitForm} onOpenChange={setShowSubmitForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              Submit Safety Suggestion
            </DialogTitle>
            <DialogDescription>Your ideas help make our workplace safer for everyone</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitSuggestion} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Brief title for your idea" required />
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Describe the safety issue or improvement opportunity..." required rows={3} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.icon} {cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Proposed Solution</Label>
              <Textarea value={formData.proposed_solution} onChange={(e) => setFormData({...formData, proposed_solution: e.target.value})} placeholder="How would you solve this?" rows={2} />
            </div>

            <div>
              <Label>Expected Benefit</Label>
              <Input value={formData.estimated_benefit} onChange={(e) => setFormData({...formData, estimated_benefit: e.target.value})} placeholder="What improvement would this bring?" />
            </div>

            <div>
              <Label>Department</Label>
              <Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="Your department (optional)" />
            </div>

            {/* Anonymous Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>Submit Anonymously</Label>
                <p className="text-xs text-gray-500">Your name won't be recorded</p>
              </div>
              <Switch checked={formData.is_anonymous} onCheckedChange={(v) => setFormData({...formData, is_anonymous: v})} />
            </div>

            {!formData.is_anonymous && (
              <div>
                <Label>Your Name</Label>
                <Input value={formData.submitter_name} onChange={(e) => setFormData({...formData, submitter_name: e.target.value})} placeholder="Enter your name" />
              </div>
            )}

            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                <Send className="w-4 h-4 mr-2" /> Submit Suggestion
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowSubmitForm(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Response Dialog (for managers) */}
      <Dialog open={showResponseForm} onOpenChange={setShowResponseForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Respond to Suggestion</DialogTitle>
            <DialogDescription>{selectedSuggestion?.title}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRespond} className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={responseData.status} onValueChange={(v) => setResponseData({...responseData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="implemented">Implemented</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes / Response</Label>
              <Textarea value={responseData.reviewer_notes} onChange={(e) => setResponseData({...responseData, reviewer_notes: e.target.value})} placeholder="Your response to this suggestion..." />
            </div>
            <div>
              <Label>Implementation Plan</Label>
              <Textarea value={responseData.implementation_plan} onChange={(e) => setResponseData({...responseData, implementation_plan: e.target.value})} placeholder="How will this be implemented?" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assigned To</Label>
                <Input value={responseData.assigned_to} onChange={(e) => setResponseData({...responseData, assigned_to: e.target.value})} placeholder="Person responsible" />
              </div>
              <div>
                <Label>Target Date</Label>
                <Input type="date" value={responseData.target_date} onChange={(e) => setResponseData({...responseData, target_date: e.target.value})} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Save Response</Button>
              <Button type="button" variant="outline" onClick={() => setShowResponseForm(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuggestionBoxPage;
